import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Sparkles, CheckCircle2, Building2, Eye, EyeOff } from "lucide-react";
import { maskPhoneBR, sanitizePhone } from "../lib/phone";

export default function Signup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    company_name: "", owner_name: "", email: "", password: "", phone: "", city: "", state: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/saas/signup", { ...form, phone: sanitizePhone(form.phone) });
      setUser({ id: data.user.id, name: data.user.name, email: data.user.email, role: data.user.role, tenant_id: data.tenant.id });
      toast.success(`Bem-vindo, ${form.owner_name}! Seu trial de 14 dias começou.`);
      nav("/onboarding", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  const BENEFITS = [
    "14 dias 100% grátis, sem cartão",
    "Acesso a todos os módulos premium",
    "Onboarding guiado em 7 passos",
    "Suporte por WhatsApp",
    "Migração de dados assistida",
  ];

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Visual side */}
      <div className="hidden lg:flex lg:w-1/2 relative theme-marketing overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1497366412874-3415097a27e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2V8ZW58MHx8fHwxNzc4NTc1Mzk2fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#14213D]/95 to-[#0A1128]" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <Link to="/saas" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md border border-[#D4AF37]/40 flex items-center justify-center">
              <span className="font-display font-bold text-[#D4AF37] text-lg">VM</span>
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-white text-base">VM Corporate</div>
              <div className="text-[10px] text-white/50 uppercase tracking-[0.2em]">Software</div>
            </div>
          </Link>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 mb-6">
              <Sparkles size={12} className="text-[#D4AF37]" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">Trial 14 dias grátis</span>
            </div>
            <h2 className="font-display text-4xl text-white font-light leading-tight">
              Comece a operar seu coworking
              <span className="block italic text-[#D4AF37] mt-2">em minutos.</span>
            </h2>
            <ul className="mt-8 space-y-3">
              {BENEFITS.map(b => (
                <li key={b} className="flex items-center gap-3 text-white/80">
                  <CheckCircle2 size={16} className="text-[#D4AF37]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 bg-white flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3">Criar conta</div>
          <h1 className="font-display text-3xl text-[#14213D] font-light mb-2">Comece seu trial gratuito</h1>
          <p className="text-sm text-gray-500 mb-6">Sem compromisso. Cancele quando quiser.</p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Nome do coworking *</label>
              <input
                required value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Ex: WorkHub São Paulo"
                className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-4 py-2.5 outline-none focus:border-[#14213D]"
                data-testid="signup-company"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Seu nome *</label>
                <input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-4 py-2.5 outline-none focus:border-[#14213D]" data-testid="signup-owner" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">WhatsApp</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhoneBR(e.target.value) })} placeholder="(21) 99999-9999" type="tel" inputMode="tel" autoComplete="tel" className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-4 py-2.5 outline-none focus:border-[#14213D]" data-testid="signup-phone" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">E-mail *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-4 py-2.5 outline-none focus:border-[#14213D]" data-testid="signup-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Senha *</label>
              <div className="relative mt-1">
                <input
                  required type={showPassword ? "text" : "password"}
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-2.5 pr-12 outline-none focus:border-[#14213D]"
                  data-testid="signup-password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded text-gray-400 hover:text-[#14213D] hover:bg-gray-100"
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  tabIndex={-1}
                  data-testid="signup-toggle-password"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="text-[10px] text-gray-400 mt-1">Mínimo 6 caracteres</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Cidade</label>
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-4 py-2.5 outline-none focus:border-[#14213D]" data-testid="signup-city" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">UF</label>
                <input maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-4 py-2.5 outline-none focus:border-[#14213D]" data-testid="signup-state" />
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-[#14213D] font-bold py-3 rounded-md hover:bg-[#F3DFA2] transition disabled:opacity-50 flex items-center justify-center gap-2 mt-2" data-testid="signup-submit">
              {loading ? "Criando sua conta…" : <>Começar trial grátis <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-gray-500">
            Já tem conta? <Link to="/login" className="text-[#14213D] font-semibold hover:text-[#D4AF37]">Entrar</Link>
          </div>
          <div className="mt-3 text-center text-xs text-gray-400">
            <Link to="/saas" className="hover:text-[#14213D]">← Conhecer a plataforma</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
